import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { Button } from 'ui-components/components/button';
import { ButtonThemeService } from 'ui-components/services/button-theme-service';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { LabelPurchaseButton } from '../../label-purchase-button';
import CreateLabelHotkey from '../../hotkeys/advanced-hotkeys/create-label';
import { CREATE_LABEL, CREATE_PRINT_LABEL, SHIPMENT_TYPES } from '../../../constants/create-label';
import { emptyArray } from 'ui-components/default-props/empty';
import { usePrepayDutiesDataFromEntities } from '../../../hooks/entities/selectors/fulfillment-plans';
import { labelMessages } from './messages';
import style from './style.module.less';

const CreateLabelButton = ({
  primaryButtonPreference = CREATE_PRINT_LABEL,
  selectedCount = 0,
  shipmentType = SHIPMENT_TYPES.standard,
  shouldBeDisabled = false,
  theme = 'primary',
  handleOnClick,
  selectedFulfillmentPlanIds = emptyArray,
  parentComponentName = '',
  containsDemoOrder = false,
}) => {
  const messagePostFix = parentComponentName || 'grid header';
  const eventTriggerSource = `Create Label button-${messagePostFix}`;

  const createShipmentTypeMessage = labelMessages[`${shipmentType}Create`] ?? labelMessages.standardCreate;
  const createAndPrintShipmentTypeMessage = labelMessages[`${shipmentType}CreateAndPrint`] ?? labelMessages.standardCreateAndPrint;

  const primaryButtonText =
    primaryButtonPreference === CREATE_LABEL ? (
      <FormattedMessage {...createShipmentTypeMessage} values={{ count: selectedCount }} />
    ) : (
      <FormattedMessage {...createAndPrintShipmentTypeMessage} values={{ count: selectedCount }} />
    );
  const secondaryButtonText =
    primaryButtonPreference === CREATE_LABEL ? (
      <FormattedMessage {...createAndPrintShipmentTypeMessage} values={{ count: selectedCount }} />
    ) : (
      <FormattedMessage {...createShipmentTypeMessage} values={{ count: selectedCount }} />
    );

  const onPrimaryButtonClick = useCallback(() => {
    const action = primaryButtonPreference === CREATE_LABEL ? CREATE_LABEL : CREATE_PRINT_LABEL;
    handleOnClick({ action });
  }, [primaryButtonPreference, handleOnClick]);
  const onSecondaryButtonClick = useCallback(() => {
    const action = primaryButtonPreference === CREATE_LABEL ? CREATE_PRINT_LABEL : CREATE_LABEL;
    handleOnClick({ action });
  }, [primaryButtonPreference, handleOnClick]);

  const { availableDutiesOptions, onSelectedDutyOptionChange, otherShippingOptions } =
    usePrepayDutiesDataFromEntities(selectedFulfillmentPlanIds);

  if (shipmentType === SHIPMENT_TYPES.external) {
    return (
      <CreateLabelHotkey disabled={shouldBeDisabled}>
        <div className={classNames('button-group', style['button-container'], style['create-and-print-label-button'])}>
          <Button
            className={style['button-styles']}
            disabled={shouldBeDisabled}
            disabledTooltipMessage={containsDemoOrder ? <FormattedMessage {...labelMessages.createLabelDisabledMessage} /> : null}
            onClick={onPrimaryButtonClick}
            theme={theme}
          >
            <FormattedMessage {...labelMessages[shipmentType]} values={{ count: selectedCount }} />
          </Button>
        </div>
      </CreateLabelHotkey>
    );
  }

  return (
    <CreateLabelHotkey disabled={shouldBeDisabled}>
      <div className={classNames('button-group', style['button-container'], style['create-and-print-label-button'])}>
        <LabelPurchaseButton
          availableDutiesOptions={availableDutiesOptions}
          canShowPrepayDutyAlert={selectedFulfillmentPlanIds?.length === 1}
          fulfillmentPlanIds={selectedFulfillmentPlanIds}
          labelPurchaseCallback={onPrimaryButtonClick}
          onSelectedDutyOptionChange={onSelectedDutyOptionChange}
          otherShippingOptions={otherShippingOptions}
          trackingId={eventTriggerSource}
        >
          {({ onClick }) => (
            <Button
              className={style['button-styles']}
              disabled={shouldBeDisabled}
              disabledTooltipMessage={containsDemoOrder ? <FormattedMessage {...labelMessages.createLabelDisabledMessage} /> : null}
              onClick={onClick}
              theme={theme}
            >
              {primaryButtonText}
            </Button>
          )}
        </LabelPurchaseButton>
        <DropdownMenu
          closeWhenChildrenClicked={false}
          disabled={shouldBeDisabled}
          dropdownMenuClassName={style['dropdown-menu']}
          menuPosition='bottom-right'
        >
          <DropdownMenuToggler className={style['drop-down-menu-button']} theme={theme} />
          <LabelPurchaseButton
            availableDutiesOptions={availableDutiesOptions}
            canShowPrepayDutyAlert={selectedFulfillmentPlanIds?.length === 1}
            fulfillmentPlanIds={selectedFulfillmentPlanIds}
            labelPurchaseCallback={onSecondaryButtonClick}
            onSelectedDutyOptionChange={onSelectedDutyOptionChange}
            otherShippingOptions={otherShippingOptions}
            trackingId={eventTriggerSource}
          >
            {({ onClick }) => <DropdownMenuItem onClick={onClick}>{secondaryButtonText}</DropdownMenuItem>}
          </LabelPurchaseButton>
        </DropdownMenu>
      </div>
    </CreateLabelHotkey>
  );
};

CreateLabelButton.propTypes = {
  primaryButtonPreference: PropTypes.string,
  selectedCount: PropTypes.number,
  shipmentType: PropTypes.string,
  shouldBeDisabled: PropTypes.bool,
  theme: PropTypes.oneOf(ButtonThemeService.getButtonThemes()),
  handleOnClick: PropTypes.func.isRequired,
  selectedFulfillmentPlanIds: PropTypes.array,
  parentComponentName: PropTypes.string,
  containsDemoOrder: PropTypes.bool,
};

export default CreateLabelButton;
